import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';

import PublicLayout from './components/common/PublicLayout';
import HomePage from './pages/HomePage';
import ProgrammesPage from './pages/ProgrammesPage';
import GalleryPage from './pages/GalleryPage';
import CollaborationPage from './pages/CollaborationPage';
import TreeBenefitsPage from './pages/TreeBenefitsPage';
import ContributorsPage from './pages/ContributorsPage';
import ContributorDetailPage from './pages/ContributorDetailPage';
import BlogPage from './pages/BlogPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import JoinPage from './pages/JoinPage';
import AdminDashboard from './pages/AdminDashboard';
import CSRDashboard from './pages/CSRDashboard';
import IndividualDashboard from './pages/IndividualDashboard';
import AgencyDashboard from './pages/AgencyDashboard';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="empty-state">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/login" />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  const getDashboardRedirect = () => {
    if (!user) return '/login';
    const routes = { admin: '/admin', csr: '/csr', individual: '/individual', agency: '/agency' };
    return routes[user.role] || '/login';
  };

  return (
    <Routes>
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/programmes" element={<PublicLayout><ProgrammesPage /></PublicLayout>} />
      <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
      <Route path="/collaboration" element={<PublicLayout><CollaborationPage /></PublicLayout>} />
      <Route path="/tree-benefits" element={<PublicLayout><TreeBenefitsPage /></PublicLayout>} />
      <Route path="/contributors" element={<PublicLayout><ContributorsPage /></PublicLayout>} />
      <Route path="/contributors/:id" element={<PublicLayout><ContributorDetailPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardRedirect()} /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={getDashboardRedirect()} /> : <RegisterPage />} />
      <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/csr/*" element={<ProtectedRoute allowedRoles={['csr']}><CSRDashboard /></ProtectedRoute>} />
      <Route path="/individual/*" element={<ProtectedRoute allowedRoles={['individual']}><IndividualDashboard /></ProtectedRoute>} />
      <Route path="/agency/*" element={<ProtectedRoute allowedRoles={['agency']}><AgencyDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

const isDemo = (process.env.REACT_APP_API_URL || '').includes('/demo');

function DemoBanner() {
  if (!isDemo) return null;
  return (
    <div style={{
      background: '#F5A623', color: '#fff', textAlign: 'center',
      padding: '6px 12px', fontSize: 12, fontWeight: 700, letterSpacing: 1.2,
      position: 'sticky', top: 0, zIndex: 9999,
    }}>
      🧪 DEMO MODE — Sample data for demonstration purposes. Data resets periodically.
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router basename={process.env.PUBLIC_URL || '/'}>
        <DemoBanner />
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} />
      </Router>
    </AuthProvider>
  );
}
